import { NgTemplateOutlet } from '@angular/common';
import {
    ChangeDetectionStrategy,
    Component,
    computed,
    effect,
    ElementRef,
    inject,
    input,
    type OnDestroy,
    type OnInit,
    signal,
    untracked,
    viewChild
} from '@angular/core';
import { NCatalogUi } from '@df/catalog-ui/df-catalog-ui.config';
import type { IProductDataV2 } from '@df/catalog/product/product';
import { App } from '@df/core/app';
import { DfImageWrapperComponent } from '@df/module-image/df-image-wrapper.component';
import { DfImageDirective } from '@df/module-image/df-image.directive';
import { KlevuTrackCategoryProductClickDirective } from '@df/module-klevu-search/klevu-track-category-product-click.directive';
import { KlevuTrackClickDirective } from '@df/module-klevu-search/klevu-track-click.directive';
import { filterMediaByLabel } from '@df/ui/common/filter-media-by-label-pipe';
import { HrefComponent } from '@df/ui/common/href/href.component';
import { Media } from '@df/ui/display/media/interfaces';
import type { VimeoPlayerComponent } from '@df/ui/display/media/vimeo-player.component';
import { TranslatePipe } from '@df/ui/translation/translate.pipe';
import { UiComponent } from '@df/ui/ui.component';
import type { Nexus } from '@tomandco/nexus-api-types';
import compact from 'lodash-es/compact';
import find from 'lodash-es/find';
import { interval, take, takeWhile } from 'rxjs';
import type { Subscription } from 'rxjs/internal/Subscription';
import { GtmTrackSelectItemDirective } from '../../../../module/gtm/gtm-track-select-item.directive';
import { LocalVimeoPlayerComponent } from '../../../../ui/display/media/vimeo-player.component';
import type { ProductDecorator } from '../../../model/product/product.decorator';
import { ProductListingVideoService } from '../../listing/product-listing-video.service';
import { ProductComponent } from '../product.component';
import { AspectRatioPipe } from '../ui/aspect-ratio.pipe';

export interface ListingImage {
    image: string;
    index?: number;
    video?: string;
}

@Component({
    selector: 'product-image',
    templateUrl: './product-image.component.html',
    styleUrl: './product-image.component.scss',
    standalone: true,
    imports: [
        AspectRatioPipe,
        DfImageDirective,
        DfImageWrapperComponent,
        HrefComponent,
        GtmTrackSelectItemDirective,
        KlevuTrackClickDirective,
        KlevuTrackCategoryProductClickDirective,
        LocalVimeoPlayerComponent,
        NgTemplateOutlet,
        TranslatePipe
    ],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class ProductImageComponent extends UiComponent implements OnInit, OnDestroy {
    readonly model = input.required<ProductDecorator<IProductDataV2>>();

    readonly carouselRef = viewChild.required<ElementRef<HTMLUListElement>>('carouselRef');

    readonly isPlaying = signal(false);
    readonly isVideoBuffered = signal(false);
    private readonly playerInstance = signal<any | undefined>(undefined);

    private videoCheckSubscription?: Subscription;
    private playerStateSubscription?: Subscription;

    private readonly app = inject(App);
    private readonly productVideoService = inject(ProductListingVideoService);
    private readonly elementRef = inject(ElementRef);
    private readonly moduleConfig = inject(NCatalogUi.OPTIONS);
    private readonly productComponent = inject(ProductComponent, { host: true });

    readonly modelView = computed(() => this.productComponent.modelView());
    readonly altView = computed(() => this.productComponent.altView());
    readonly disableCarousel = computed(() => this.productComponent.disableCarousel());
    readonly priority = computed(() => this.productComponent.priority());
    readonly wishlistCarouselItem = computed(() => this.productComponent.wishlistCarouselItem());
    readonly isAccountPage = computed(() => this.productComponent.isAccountPage());
    readonly klevuSearchTrackClick = computed(() => this.productComponent.klevuSearchTrackClick());
    readonly klevuCategoryProductTrackClick = computed(() => this.productComponent.klevuCategoryProductTrackClick());
    readonly gtmTrackSelectItem = computed(() => this.productComponent.gtmTrackSelectItem);
    readonly hrefLink = computed(() => this.productComponent.hrefLink());
    readonly listPosition = computed(() => this.productComponent.listPosition || 0);
    readonly gridColumn = computed(() => this.productComponent.gridColumn || 1);
    readonly gridRowSpan = computed(() => this.productComponent.gridRowSpan || 1);
    readonly playerIdentifier = computed(() => `video.${this.model().id}.${this.listPosition()}`);

    readonly sizes = computed(() => {
        const sizes = this.productComponent.sizes();
        const listing = this.productComponent.listing;

        if (sizes) {
            return sizes;
        }

        const data = this.model().data;

        if (data && listing) {
            const colSpan = Math.max(1, Math.min(data.col_span || 1, listing?.size?.getState() || 1));
            const defaultSize = listing?.size?.defaultSize || this.moduleConfig.listing.category.defaultSize || [2, 2, 5, 5];

            const currentSizeS = listing?.size?.currentSize[0] || 0;
            const defaultSizeS = currentSizeS !== 0 && currentSizeS !== defaultSize[0] ? currentSizeS : defaultSize[0];
            const sizeS = `${Math.min(100, Math.max(20, Math.round(100 / defaultSizeS) * colSpan))}vw`;

            const currentSizeM = listing?.size?.currentSize[1] || 0;
            const defaultSizeM = currentSizeM !== 0 && currentSizeM !== defaultSize[1] ? currentSizeM : defaultSize[1];
            const sizeM = `${Math.min(100, Math.max(20, Math.round(100 / defaultSizeM) * colSpan))}vw`;

            const currentSizeL = listing?.size?.currentSize[2] || 0;
            const defaultSizeL = currentSizeL !== 0 && currentSizeL !== defaultSize[2] ? currentSizeL : defaultSize[2];
            const sizeL = `${Math.min(100, Math.max(20, Math.round(100 / defaultSizeL) * colSpan))}vw`;

            const currentSizeX = listing?.size?.currentSize[3] || 0;
            const defaultSizeX = currentSizeX !== 0 && currentSizeX !== defaultSize[3] ? currentSizeX : defaultSize[3];
            const sizeX = `${Math.min(100, Math.max(20, Math.round(100 / defaultSizeX) * colSpan))}vw`;

            return [sizeS, sizeM, sizeL, sizeX];
        }

        return undefined;
    });

    readonly portraitImages = computed(() => {
        const colorInfo = this.productComponent.getColorInfo();
        const media = this.getMedia();

        if (!media?.length) {
            return [{ image: colorInfo?.image || this.model().data?.image, video: undefined }];
        }

        // setting up base and alternative image
        let listingPortraitMediaModels = compact(
            this.modelView()
                ? [
                      this.getMediaItemByLabel(media, 'model-base') || this.getMediaItemByLabel(media, 'base'),
                      this.getMediaItemByLabel(media, 'model-alternative') || this.getMediaItemByLabel(media, 'alternative')
                  ]
                : [this.getMediaItemByLabel(media, 'base'), this.getMediaItemByLabel(media, 'alternative')]
        );

        // display `*-alternative` as base image
        if (this.altView()) {
            listingPortraitMediaModels = listingPortraitMediaModels.reverse();
        }

        // adding model images
        const modelItems = filterMediaByLabel(media, 'hero', false) || [];
        modelItems.forEach(modelItem => {
            if (!listingPortraitMediaModels.find(i => i && i.image === modelItem.image)) {
                listingPortraitMediaModels.push(modelItem);
            }
        });

        if (listingPortraitMediaModels.length) {
            return compact(
                listingPortraitMediaModels.map((item, index) => {
                    if (item) {
                        return {
                            image: item.image,
                            index,
                            video: item.video,
                            labels: item.labels
                        };
                    }

                    return undefined;
                })
            );
        }

        return [{ image: colorInfo?.image || this.model().data?.image, video: undefined }];
    });

    readonly landscapeImages = computed(() => {
        const colorInfo = this.productComponent.getColorInfo();
        const media = this.getMedia();
        let i = 0;

        if (!media?.length) {
            return [{ image: colorInfo?.image || this.model().data?.image, video: undefined }];
        }

        if (this.model().colSpan > 1) {
            const listingLandscapeImages = media
                ?.map(item => {
                    if (item.labels?.find(label => this.isLandscapeImage(label))) {
                        const index = this.hasAltLandscape() && this.modelView() ? 1 : 0;
                        return {
                            image: item.image,
                            index,
                            video: item.video
                        };
                    }

                    if (item.labels?.find(label => this.isLandscapeHoverImage(label))) {
                        i = i + 1;
                        const index = this.hasAltLandscape() && this.modelView() ? 0 : 1;
                        return {
                            image: item.image,
                            index,
                            video: item.video
                        };
                    }

                    if (item.labels?.find(label => label.match('hero'))) {
                        i = i + 1;
                        return {
                            image: item.image,
                            index: 2,
                            video: item.video
                        };
                    }
                })
                .filter(image => !!image)
                .sort((a, b) => a.index - b.index);

            if (listingLandscapeImages?.length) {
                return listingLandscapeImages;
            }
        }

        return [{ image: colorInfo?.image || this.model().data?.image, video: undefined }];
    });

    /**
     * Returns correct listing images
     */
    readonly images = computed(() => {
        return this.model().isLandscapeListingView ? this.landscapeImages() : this.portraitImages();
    });

    /**
     * Retrieve first image available
     */
    readonly primaryImage = computed(() => {
        const images = this.images();

        if (images.length) {
            return images[0]?.video ? this.findFirstImage() : images[0]?.image;
        }

        return undefined;
    });

    /**
     * Retrieve first video available
     */
    readonly primaryVideo = computed(() => {
        const images = this.images();

        if (images.length) {
            return images[0]?.video;
        }

        return undefined;
    });

    /**
     * Returns correct listing images for slider
     */
    readonly alternativeImages = computed(() => {
        const images = this.images();

        if (images.length) {
            return images.slice(1);
        }

        return images || [];
    });

    readonly alternativeImagesLength = computed(() => {
        return this.alternativeImages()?.length || 0;
    });

    readonly dots = computed(() => {
        if (this.isTouchDevice()) {
            return this.images();
        }

        return this.alternativeImages();
    });

    readonly isTouchDevice = computed(() => {
        if (!this.app.isPlatformBrowser) {
            return this.client.isS || this.client.isM
        }
        
        return this.app.client.isTouchy || window.matchMedia('(pointer: coarse)').matches || 'ontouchstart' in window;
    });

    readonly canShowAlternativeImages = computed(() => {
        return this.alternativeImagesLength() === 1 && this.app.isPlatformBrowser && !this.isTouchDevice();
    });

    /**
     * If carousel can be printed
     */
    readonly canShowCarousel = computed(() => {
        return !this.model().data?.disable_carousel && !this.disableCarousel() && this.alternativeImagesLength() > 0;
    });

    /**
     * Show carousel on hover (desktop) or always (mobile)
     */
    readonly showCarouselOnHover = computed(() => {
        return this.canShowCarousel() && !this.isTouchDevice() && this.alternativeImagesLength() > 1;
    });

    /**
     * Show carousel always (mobile with touch)
     */
    readonly showCarouselAlways = computed(() => {
        return this.canShowCarousel() && this.isTouchDevice();
    });

    /**
     * Get first not video
     */
    private readonly findFirstImage = computed(() => {
        return this.images().find(media => !media.video)?.image;
    });

    constructor() {
        super();

        effect(() => {
            const carouselRef = this.carouselRef();

            if (!carouselRef) return;

            untracked(() => {
                carouselRef.nativeElement.removeEventListener('scroll', this.onScroll);
                carouselRef.nativeElement.addEventListener('scroll', this.onScroll, { passive: true });
            });

            return () => {
                carouselRef.nativeElement.removeEventListener('scroll', this.onScroll);
            };
        });
    }

    override ngOnInit() {
        this.updateVideoObserver();

        super.ngOnInit();
    }

    override ngOnDestroy() {
        this.videoCheckSubscription?.unsubscribe();
        this.videoCheckSubscription = undefined;

        this.productVideoService.unsubscribe(this.elementRef);

        const container = this.carouselRef().nativeElement;

        if (container) {
            container.removeEventListener('scroll', this.onScroll);
        }

        super.ngOnDestroy();
    }

    protected next(): void {
        const container = this.carouselRef().nativeElement;

        // Temporarily disable scroll-snap for precise scrolling
        const previousSnapType = container.style.scrollSnapType;
        container.style.scrollSnapType = 'none';

        container.scrollBy({ left: container.clientWidth, behavior: 'auto' });

        // Re-enable scroll-snap after scroll completes
        requestAnimationFrame(() => {
            container.style.scrollSnapType = previousSnapType;
        });
    }

    protected prev(): void {
        const container = this.carouselRef().nativeElement;

        // Temporarily disable scroll-snap for precise scrolling
        const previousSnapType = container.style.scrollSnapType;
        container.style.scrollSnapType = 'none';

        container.scrollBy({ left: -container.clientWidth, behavior: 'auto' });

        // Re-enable scroll-snap after scroll completes
        requestAnimationFrame(() => {
            container.style.scrollSnapType = previousSnapType;
        });
    }

    protected scrollToIndex(index: number) {
        if (index < 0 || index >= this.images().length) return;

        const container = this.carouselRef().nativeElement;

        const slideWidth = container.clientWidth;
        container.scrollLeft = slideWidth * (index + 1);
    }

    protected onPlayerInit(component: VimeoPlayerComponent) {
        this.playerInstance.set(component.playerInstance);
    }

    protected onVideoEnd(): void {
        this.productVideoService.notifyEnd();
    }

    protected onBufferEnd(): void {
        if (!this.isVideoBuffered()) {
            this.isVideoBuffered.set(true);

            this.detectChanges();
        }
    }

    protected hasAltLandscape(): boolean {
        const media = this.getMedia();

        if (media?.length) {
            const landscapeImage = find(media, item => item.labels?.find(label => this.isLandscapeImage(label)));
            const landscapeHoverImage = find(media, item => item.labels?.find(label => this.isLandscapeHoverImage(label)));
            return !!(landscapeImage && landscapeHoverImage);
        }

        return false;
    }

    protected getMedia() {
        const colorInfo = this.productComponent.getColorInfo();
        return colorInfo?.image || colorInfo?.media ? colorInfo?.media : this.model().data?.media;
    }

    private updateVideoObserver() {
        if (!this.app.isPlatformBrowser) {
            return;
        }

        const hasVideo = !!this.primaryVideo;

        if (!!this.videoCheckSubscription !== hasVideo) {
            // if
            if (hasVideo) {
                this.videoCheckSubscription = this.productVideoService
                    .observe(this.elementRef, this.listPosition(), this.playerIdentifier())
                    .subscribe((isPlaying: boolean) => {
                        let isPaused = true;

                        if (this.isPlaying() !== isPlaying) {
                            this.playerStateSubscription?.unsubscribe();
                        }

                        this.isPlaying.set(isPlaying);
                        this.isVideoBuffered.set(isPlaying);

                        this.app.events.broadcast(isPlaying ? Media.Events.play : Media.Events.stop, this.playerIdentifier);

                        if (isPlaying) {
                            this.playerStateSubscription = interval(1000)
                                .pipe(
                                    takeWhile(() => isPaused || (isPaused === undefined && isPlaying)),
                                    take(3)
                                )
                                .subscribe(async () => {
                                    this.app.events.broadcast(Media.Events.play, this.playerIdentifier);

                                    isPaused = await this.playerInstance()?.player?.getPaused();
                                });
                        }

                        this.detectChanges();
                    });
            } else {
                this.videoCheckSubscription?.unsubscribe();
                this.videoCheckSubscription = undefined;
            }
        }
    }

    private isLandscapeImage(label: string): boolean {
        return label === 'landscape';
    }

    private isLandscapeHoverImage(label: string): boolean {
        return !!label.match('landscape hover');
    }

    private getMediaItemByLabel(media, label: string): Nexus.Lib.Catalog.Type.IProductMediaItem | undefined {
        const items = filterMediaByLabel(media, label, false);

        return items && items[0];
    }

    private onScroll = (): void => {
        this.updateCurrentIndexOnScroll();
    };

    private updateCurrentIndexOnScroll(): void {
        const container = this.carouselRef().nativeElement;

        const slideWidth = container.clientWidth;

        if (slideWidth === 0) return;

        const scrollIndex = Math.round(container.scrollLeft / slideWidth);

        const carouselDots = this.elementRef.nativeElement.querySelector('.carousel-dots-inner');

        if (!carouselDots) return;

        // remove `isActive` class from all dots
        const dots = carouselDots.querySelectorAll('.carousel-dot') as NodeListOf<HTMLButtonElement>;
        dots.forEach(dot => {
            dot.classList.remove('is-active');
        });

        // set `isActive` class on dot with index `scrollIndex`
        const dot = carouselDots.querySelector(`.carousel-dot:nth-of-type(${scrollIndex + 1})`);

        if (dot) {
            dot.classList.add('is-active');
        }

        // hide previous and next buttons
        const previousButton = this.elementRef.nativeElement.querySelector('.carousel-prev');
        const nextButton = this.elementRef.nativeElement.querySelector('.carousel-next');

        if (previousButton && nextButton) {
            previousButton.classList.remove('opacity-0');
            nextButton.classList.remove('opacity-0');

            if (scrollIndex === 0) {
                previousButton.classList.add('opacity-0');
            }

            if (scrollIndex === this.alternativeImagesLength() - 1) {
                nextButton.classList.add('opacity-0');
            }
        }
    }
}
